/**
 * @file CategorySlugSeoFields.tsx
 * @description Адрес и SEO категории в окне правки (phase19.16, партия Р3в,
 * v0.19.64; SB131, SEO11, SEO12): поле адреса, при его смене у сохранённой
 * категории — галочка «Переадресовать со старого адреса» (по умолчанию да),
 * и «Ключевые запросы» — только у категорий магазина (у категорий платформы
 * их нет: строка общая для всех магазинов). Вынесено из `CategoryEditDialog`
 * (компонент ≤ 200 строк) с общим контрактом `value/onChange`.
 */

import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { RedirectOldSlugCheck } from "../site-seo/RedirectOldSlugCheck";
import { SeoKeywordsField } from "../site-seo/SeoKeywordsField";
import type { SeoKeywordConflict, SeoKeywordsValue } from "../../lib/site-seo/keywords";

export interface CategorySlugSeoValue {
  slug: string;
  redirectFromOldSlug: boolean;
  keywords: SeoKeywordsValue;
}

interface Props {
  value: CategorySlugSeoValue;
  onChange: (patch: Partial<CategorySlugSeoValue>) => void;
  /** Адрес сохранённой категории; новая — null. */
  savedSlug: string | null;
  /** Категория магазина (не платформы) — есть ключевые запросы. */
  shopOwned: boolean;
  /** Название — для проверки «запроса нет в заголовке». */
  title: string;
  conflicts: readonly SeoKeywordConflict[];
  disabled: boolean;
}

export function CategorySlugSeoFields({ value, onChange, savedSlug, shopOwned, title, conflicts, disabled }: Props) {
  const changed = !!savedSlug && value.slug.trim() !== "" && value.slug.trim() !== savedSlug;
  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="c-slug">Адрес категории на сайте</Label>
        <Input id="c-slug" value={value.slug} onChange={(e) => onChange({ slug: e.target.value.toLowerCase() })} disabled={disabled} />
        <p className="text-xs text-on-surface-variant">Оставьте пустым — сгенерируется из названия.</p>
        <RedirectOldSlugCheck
          oldPath={`/catalog/${savedSlug ?? ""}`}
          changed={changed}
          checked={value.redirectFromOldSlug}
          onChange={(redirectFromOldSlug) => onChange({ redirectFromOldSlug })}
          disabled={disabled}
        />
      </div>
      {shopOwned ? (
        <fieldset className="flex min-w-0 flex-col gap-1.5">
          <legend className="text-sm font-medium text-on-surface">Ключевые запросы</legend>
          <SeoKeywordsField value={value.keywords} onChange={(keywords) => onChange({ keywords })} title={title} h1={title} conflicts={conflicts} disabled={disabled} />
        </fieldset>
      ) : null}
    </>
  );
}
